import * as React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  EditNote as AuditIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";

import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const DRAWER_WIDTH = 260;

// Navigation structure with role-based access
const NAV_ITEMS = [
  {
    label: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    roles: ["admin", "coordinator", "viewer", "tutor"],
  },
  {
    label: "Logger",
    icon: <AssignmentIcon />,
    path: "/logger",
    roles: ["tutor"],
  },
  {
    label: "Tutors",
    icon: <PeopleIcon />,
    path: "/tutors",
    roles: ["admin", "coordinator", "viewer"],
  },
  {
    label: "Students",
    icon: <SchoolIcon />,
    path: "/students",
    roles: ["admin", "coordinator", "viewer"],
  },
  {
    label: "Sessions",
    icon: <EventIcon />,
    path: "/sessions",
    roles: ["admin", "coordinator", "viewer", "tutor"],
  },
  {
    label: "Audit Logs",
    icon: <AuditIcon />,
    path: "/audit-logs",
    roles: ["admin", "coordinator", "viewer"],
  },
];

export default function Layout() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const formatRole = (r) =>
    r ? r.charAt(0).toUpperCase() + r.slice(1) : "User";

  const getInitials = (n) =>
    n
      ? n
          .split(" ")
          .map((i) => i[0])
          .join("")
          .toUpperCase()
      : "U";

  const visibleNavItems = NAV_ITEMS.filter(({ roles }) => {
    const hasRole = roles.includes(profile?.role);
    if (profile?.role === "tutor")
      return hasRole && profile?.status === "approved";
    return hasRole;
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
      }}
    >
      {/* BRANDING AREA */}
      <Toolbar sx={{ px: [2, 3], mt: 1, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.5px",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            lineHeight: 1.15,
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
              borderRadius: "12px",
              display: "flex",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: "1.2rem",
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
            }}
          ></Box>
          Tutors @ Your Service
        </Typography>
      </Toolbar>

      <Box sx={{ px: 2, py: 1 }}>
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, color: "text.disabled", pl: 1 }}
        >
          {formatRole(profile?.role)} Menu
        </Typography>
      </Box>

      <List sx={{ px: 2, py: 0 }}>
        {visibleNavItems.map((item) => {
          const isActive = pathname === item.path;
          return (
            <ListItemButton
              key={item.label}
              selected={isActive}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                mb: 0.5,
                color: isActive ? "primary.main" : "text.secondary",
                bgcolor: isActive ? "rgba(99, 102, 241, 0.08)" : "transparent",
                borderRadius: "12px",
                "&.Mui-selected": {
                  bgcolor: "rgba(99, 102, 241, 0.12)",
                  "&:hover": { bgcolor: "rgba(99, 102, 241, 0.16)" },
                },
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)", // Standard neutral hover
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isActive ? "primary.main" : "inherit",
                }}
              >
                {React.cloneElement(item.icon, { fontSize: "small" })}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 500,
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <CssBaseline />

      {/* TOP BAR */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "rgba(255, 255, 255, 0.7)", // Semi-transparent white
          backdropFilter: "blur(12px)", // The frosted glass effect
          WebkitBackdropFilter: "blur(12px)", // For Safari support
          border: "1px solid",
          borderRadius: 12,
          borderColor: "rgba(0, 0, 0, 0.06)", // Extremely subtle border
          color: "text.primary",
          left: {
            xs: "50%",
            md: `calc(50% + ${DRAWER_WIDTH / 2}px)`, // Position from the left, accounting for the drawer width
          },
          transform: "translateX(-50%)",
          top: 0,
          width: {
            xs: `calc(100%)`,
            md: `calc(100% - ${DRAWER_WIDTH}px - 60px)`,
          },
        }}
      >
        <Toolbar
          sx={{ justifyContent: { xs: "space-between", md: "flex-end" } }}
        >
          {/* Hamburger Icon - Only visible on mobile */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{ textAlign: "right", display: { xs: "none", md: "block" } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {profile?.full_name || "User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: -0.5 }}
              >
                {formatRole(profile?.role)}
              </Typography>
            </Box>

            <Tooltip title="Profile Settings">
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  p: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "0.2s",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                <Avatar
                  src={profile?.avatar_url}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.875rem",
                    bgcolor: "primary.main",
                  }}
                >
                  {getInitials(profile?.full_name)}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* DRAWER */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              borderRight: "none",
              bgcolor: "background.paper",
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              borderTopRightRadius: 20,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* MAIN CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar /> {/* Spacer below AppBar */}
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>

      {/* PROFILE MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1.5,
              borderRadius: 3,
              minWidth: 200,
              overflow: "visible",
              filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.08))",
              border: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {profile?.full_name || "User"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatRole(profile?.role)}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.5,
            mx: 2,
            my: 0.5,
            borderRadius: "12px",
            color: "error.main",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: "error.light",
              color: "error.dark",
              "& .MuiListItemIcon-root": {
                transform: "translateX(3px)",
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <LogoutIcon
              fontSize="small"
              sx={{
                color: "error.main",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Logout
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
